import { useMemo, useState } from 'react';
import {
  ArrowLeft,
  BookOpenText,
  Check,
  ChevronRight,
  Copy,
  Download,
  KeyRound,
  Search,
  ShieldCheck,
  Sparkles,
  Zap,
} from 'lucide-react';
import openApiSource from '../../docs/openapi.yaml?raw';

type Endpoint = {
  id: string;
  group: string;
  method: 'GET' | 'POST' | 'PUT' | 'HEAD' | 'PATCH';
  headers?: Record<string, string>;
  anonymous?: boolean;
  path: string;
  title: string;
  permission: string;
  summary: string;
  request?: string;
  response: string;
  notes?: string[];
};

const ENDPOINTS: Endpoint[] = [
  {
    id: 'health', group: '服务', method: 'GET', path: '/api/health', title: '检查服务健康状态', permission: '无需 key', anonymous: true,
    summary: '检查 Worker 和 D1 数据库是否可用，不需要 API key。',
    response: '{\n  "data": {\n    "status": "ok",\n    "database": true,\n    "time": "2026-09-19T08:00:00.000Z"\n  },\n  "requestId": "..."\n}',
    notes: ['响应不缓存', '仅用于连通性检查，不代表具体业务接口一定可用'],
  },
  {
    id: 'ip-echo-json', group: 'IP Echo', method: 'GET', path: '/api/ip/json', title: '查询来源地址（JSON）', permission: '无需 key', anonymous: true,
    summary: '返回当前请求的 IPv4 和 IPv6 来源地址，不需要 API key，响应不缓存。',
    response: '{\n  "data": {\n    "ip": "203.0.113.10",\n    "ipv6": "2001:db8::10"\n  },\n  "requestId": "..."\n}',
    notes: ['IPv6 来源取 Cloudflare 的 CF-Connecting-IPv6', 'IPv6 单栈请求时 ipv6 与 ip 相同'],
  },
  {
    id: 'ip-echo-text', group: 'IP Echo', method: 'GET', path: '/api/ip', title: '查询来源地址（纯文本）', permission: '无需 key', anonymous: true,
    summary: '以纯文本返回当前请求的来源地址，每行一个地址，不需要 API key，响应不缓存。',
    response: '203.0.113.10\n2001:db8::10',
    notes: ['双栈时依次返回 ip、ipv6 两行', '单栈请求只返回一行'],
  },
  {
    id: 'ddns-update-legacy', group: '域名', method: 'GET', path: '/api/ddns/update?ip=1.1.1.1', title: '兼容 DDNS 更新', permission: 'ddns:write',
    summary: '为只识别 good/nochg 纯文本结果的路由器提供 DDNS 更新兼容入口。',
    response: 'good 1.1.1.1',
    notes: ['响应为 good <ip> 或 nochg <ip>', '省略 ip 时使用请求来源地址', '新客户端优先使用 GET/POST /api/ddns'],
  },
  {
    id: 'ddns-get', group: '域名', method: 'GET', path: '/api/ddns?ip=1.1.1.1', title: 'GET 更新 DDNS', permission: 'ddns:write',
    summary: '通过查询参数更新当前 API key 所属子域的 A 或 AAAA 记录。省略 ip 时使用请求来源 IP。',
    response: '{\n  "data": {\n    "status": "updated",\n    "ip": "1.1.1.1",\n    "type": "A",\n    "fqdn": "nas.188581.xyz"\n  },\n  "requestId": "..."\n}',
    notes: ['仅接受公网单播 IPv4 或 IPv6', 'IPv6 示例：?ip=2606:4700:4700::1111，更新 AAAA 记录', '子域关闭 IPv6 DDNS 时返回 DDNS_IPV6_DISABLED', 'status 为 updated 或 unchanged', '响应格式与 POST /api/ddns 相同'],
  },
  {
    id: 'ddns', group: '域名', method: 'POST', path: '/api/ddns', title: '更新 DDNS', permission: 'ddns:write',
    summary: '更新当前 API key 所属子域的 A 或 AAAA 记录。省略 ip 时使用请求来源 IP。',
    request: '{\n  "ip": "1.1.1.1"\n}',
    response: '{\n  "data": {\n    "status": "updated",\n    "ip": "1.1.1.1",\n    "type": "A",\n    "fqdn": "nas.188581.xyz"\n  },\n  "requestId": "..."\n}',
    notes: ['仅接受公网单播 IPv4 或 IPv6', 'IPv6 请求体示例：{"ip":"2606:4700:4700::1111"}', '子域关闭 IPv6 DDNS 时返回 DDNS_IPV6_DISABLED', 'status 为 updated 或 unchanged'],
  },
  {
    id: 'data-list', group: '命名数据', method: 'GET', path: '/api/data', title: '列出命名数据', permission: 'data:read',
    summary: '按游标列出当前子域最近更新的命名数据元信息。',
    response: '{\n  "data": {\n    "items": [{\n      "item_key": "device/state",\n      "content_type": "application/json",\n      "size_bytes": 34,\n      "version": 2,\n      "updated_at": "2026-09-19T08:00:00.000Z"\n    }],\n    "nextCursor": "eyJ1cGRhdGVkQXQiOiIyMDI2..."\n  },\n  "requestId": "..."\n}',
    notes: ['limit 默认 100，最大 200', '下一页将 nextCursor 原样传入 cursor；没有下一页时为 null'],
  },
  {
    id: 'data-get', group: '命名数据', method: 'GET', path: '/api/data/{name}', title: '读取命名数据', permission: 'data:read',
    summary: '读取 JSON 或纯文本原始正文。此接口不使用标准 JSON 响应包装。',
    response: '{"online":true,"temperature":41.2}',
    notes: ['响应 Content-Type 保留写入时声明的类型', '响应包含 ETag 和 Last-Modified'],
  },
  {
    id: 'data-get-update', group: '命名数据', method: 'GET', path: '/api/data/{name}?value={value}', title: 'GET 简易更新数据', permission: 'data:write',
    summary: '为只能发起简单 GET 请求的设备新建或覆盖纯文本数据。',
    response: '{\n  "data": {\n    "name": "device-status",\n    "size": 3,\n    "updatedAt": "2026-09-19T08:00:00.000Z"\n  },\n  "requestId": "..."\n}',
    notes: ['value 允许空字符串，UTF-8 编码后最大 256 KiB', '响应包含 Cache-Control: no-store', '敏感或较大内容应使用 PUT，避免值进入外部访问日志或受到 URL 长度限制'],
  },
  {
    id: 'data-put', group: '命名数据', method: 'PUT', path: '/api/data/{name}', title: '写入命名数据', permission: 'data:write',
    summary: '新建或覆盖 JSON/纯文本数据，name 最长 128 个字符且不能包含 ..。',
    request: '{\n  "online": true,\n  "temperature": 41.2\n}',
    response: '{\n  "data": {\n    "name": "device/state",\n    "size": 37,\n    "updatedAt": "2026-09-19T08:00:00.000Z"\n  },\n  "requestId": "..."\n}',
    notes: ['Content-Type 只能是 application/json 或 text/plain', '请求体最大 256 KiB'],
  },
  {
    id: 'events-list', group: '事件', method: 'GET', path: '/api/events?limit=50&cursor={cursor}&type={type}', title: '读取事件', permission: 'events:read',
    summary: '按递减 id 游标读取追加事件，可按事件类型过滤。',
    response: '{\n  "data": {\n    "items": [{\n      "id": 128,\n      "event_type": "backup.completed",\n      "payload": {"bytes": 1048576},\n      "created_at": "2026-09-19T08:00:00.000Z"\n    }],\n    "nextCursor": 128\n  },\n  "requestId": "..."\n}',
    notes: ['limit 默认 50，最大 200', '下一页继续传入 nextCursor'],
  },
  {
    id: 'events-create', group: '事件', method: 'POST', path: '/api/events', title: '追加事件', permission: 'events:write',
    summary: '追加一个带任意 JSON payload 的事件。',
    request: '{\n  "type": "backup.completed",\n  "payload": {"file": "backup.tar", "bytes": 1048576},\n  "clientTimestamp": "2026-09-19T08:00:00.000Z"\n}',
    response: '{\n  "data": {\n    "id": 128,\n    "createdAt": "2026-09-19T08:00:01.000Z"\n  },\n  "requestId": "..."\n}',
    notes: ['type 必填，最长 80 个字符', '请求体最大 64 KiB'],
  },
  {
    id: 'logger-get', group: '消息与通知', method: 'GET', path: '/api/logger?value={value}&level={level}', title: 'GET 写入 Logger 日志', permission: 'logger:write',
    summary: '为只能发起简单 GET 请求的设备持久化日志并实时广播到管理台 Logger。',
    response: '{\n  "data": {\n    "id": 42,\n    "level": "info",\n    "message": "backup completed"\n  },\n  "requestId": "..."\n}',
    notes: ['value 必填', 'level 可选，支持 debug、info、warn、error，缺失或无效时使用 info', '敏感或较长内容应使用 POST'],
  },
  {
    id: 'logger', group: '消息与通知', method: 'POST', path: '/api/logger', title: '写入 Logger 日志', permission: 'logger:write',
    summary: '持久化日志并实时广播到管理台 Logger。',
    request: '{\n  "level": "info",\n  "message": "backup completed",\n  "source": "nas-agent",\n  "metadata": {"durationSeconds": 12}\n}',
    response: '{\n  "data": {\n    "id": 42,\n    "level": "info",\n    "message": "backup completed",\n    "source": "nas-agent"\n  },\n  "requestId": "..."\n}',
    notes: ['level 支持 debug、info、warn、error，缺失或无效时使用 info', 'message 最长 16384 个字符'],
  },
  {
    id: 'console-pull', group: 'Interactive Console', method: 'GET', path: '/api/console/commands?consoleId=default&limit=20', title: '拉取 Console 命令', permission: 'console:read',
    summary: '轮询当前子域指定 Console 的命令队列。未回复的命令会在后续轮询中继续返回；省略 consoleId 使用 default，可为同一子域运行多个 Console。',
    response: '{\n  "data": {\n    "consoleId": "default",\n    "items": [{\n      "commandId": "...",\n      "consoleId": "default",\n      "command": "uname -a",\n      "status": "running"\n    }]\n  }\n}',
    notes: ['consoleId 只能包含字母、数字、点、下划线、冒号和连字符，最长 80 个字符', 'limit 默认 20，最大 50', '命令按入队顺序返回'],
  },
  {
    id: 'console-ack', group: 'Interactive Console', method: 'POST', path: '/api/console/commands/{commandId}/ack', title: '回复 Console 命令', permission: 'console:ack',
    summary: '将 Agent 的命令输出回复到管理台，接口幂等。',
    request: '{\n  "status": "success",\n  "responseType": "text",\n  "response": "Linux edge 6.8"\n}',
    response: '{\n  "data": {\n    "acknowledged": true,\n    "status": "completed"\n  }\n}',
    notes: ['responseType 支持 text、json', '同一命令重复回复不会覆盖首次结果'],
  },
  {
    id: 'notifications-get', group: '消息与通知', method: 'GET', path: '/api/notifications?channel=telegram&text={text}&silent=false', title: '发送简单文本通知', permission: 'notifications:send',
    summary: '通过查询参数发送纯文本通知；当前支持 Telegram。',
    response: '{\n  "data": {"sent": true, "channel": "telegram", "type": "text"},\n  "requestId": "..."\n}',
    notes: ['channel 和 text 必填', '敏感内容应改用 POST，避免通知内容进入 URL', '与 POST 共用每个 key 每分钟 10 次的限额'],
  },
  {
    id: 'notifications', group: '消息与通知', method: 'POST', path: '/api/notifications', title: '发送通知', permission: 'notifications:send',
    summary: '通过显式选择的已配置通道发送纯文本、Markdown 或 HTML 通知；当前支持 Telegram。',
    request: '{\n  "channel": "telegram",\n  "type": "md",\n  "text": "*备份任务*已完成",\n  "silent": false\n}',
    response: '{\n  "data": {"sent": true, "channel": "telegram", "type": "md"},\n  "requestId": "..."\n}',
    notes: ['type 支持 text、md、html，默认为 text', 'Telegram 的 md 使用 MarkdownV2', '文本最长 4096 个字符', '每个 key 每分钟最多 10 次'],
  },
  {
    id: 'files-upload', group: '文件', method: 'POST', path: '/api/upload?path=backups%2F2026%2Flatest.json', title: '便捷上传文件', permission: 'files:write（建目录时还需 files:manage）',
    summary: '将原始正文上传到当前 key 所属文件空间，自动创建缺失的父目录。',
    request: '<binary body>',
    response: '{\n  "data": {\n    "id": "FILE_ID",\n    "path": "backups/2026/latest.json",\n    "kind": "file",\n    "etag": "\\"FILE_ID:1\\"",\n    "size_bytes": 1024\n  },\n  "requestId": "..."\n}',
    notes: ['POST 和 PUT 均可用，最大 100 MiB', '已存在文件默认覆盖；可使用 If-None-Match 或 If-Match 保护版本', '固定写入 self 空间，不能指定全局或其他子域'],
  },
  {
    id: 'files-upload-put', group: '文件', method: 'PUT', path: '/api/upload?path=backups%2F2026%2Flatest.json', title: '便捷上传文件（PUT）', permission: 'files:write（建目录时还需 files:manage）',
    summary: '使用 PUT 将原始正文上传到当前 key 所属文件空间，行为与 POST /api/upload 相同。',
    request: '<binary body>',
    response: '{\n  "data": {\n    "id": "FILE_ID",\n    "path": "backups/2026/latest.json",\n    "kind": "file",\n    "etag": "\\"FILE_ID:1\\"",\n    "size_bytes": 1024\n  },\n  "requestId": "..."\n}',
    notes: ['最大 100 MiB；自动创建父目录时还需 files:manage', '已存在文件默认覆盖，可使用 If-None-Match 或 If-Match 保护版本'],
  },
  {
    "id": "files-space",
    "method": "GET",
    "path": "/api/file-spaces/self",
    "title": "读取文件空间",
    "permission": "任一 files:* 能力",
    "summary": "读取本子域文件空间和 key 的能力。global 空间需要对应 files:global:* 权限。",
    "response": "{\"data\":{\"id\":\"domain:...\",\"visibility\":\"private\",\"etag\":\"\\\"domain:...:1\\\"\",\"capabilities\":[\"read\",\"write\"]}}",
    "group": "文件"
  },
  {
    "id": "files-list",
    "method": "GET",
    "path": "/api/file-spaces/self/entries?limit=100",
    "title": "浏览目录与搜索",
    "permission": "files:read",
    "summary": "parentId 指定目录，省略为根目录；search 在整个空间按名称搜索。",
    "response": "{\"data\":{\"items\":[],\"nextCursor\":null},\"requestId\":\"...\"}",
    "notes": [
      "每页默认 100、最多 200 项",
      "使用 nextCursor 继续读取，保持空间、目录和搜索条件不变"
    ],
    "group": "文件"
  },
  {
    "id": "files-folder",
    "method": "POST",
    "path": "/api/file-spaces/self/folders",
    "title": "新建文件夹",
    "permission": "files:manage",
    "summary": "创建空文件夹，默认继承上级权限。显式设置公开或私有需要 files:publish。",
    "request": "{\"name\":\"backups\",\"parentId\":null}",
    "response": "{\"data\":{\"id\":\"FOLDER_ID\",\"name\":\"backups\",\"kind\":\"folder\",\"visibility\":\"inherit\"}}",
    "group": "文件"
  },
  {
    "id": "files-detail",
    "method": "GET",
    "path": "/api/file-spaces/self/entries/{id}",
    "title": "读取节点详情",
    "permission": "files:read",
    "summary": "返回完整路径、实际权限及来源、面包屑和 etag。",
    "response": "{\n  \"data\": {\n    \"id\": \"FILE_ID\",\n    \"path\": \"backup.json\",\n    \"kind\": \"file\",\n    \"visibility\": \"inherit\",\n    \"effectiveVisibility\": \"private\",\n    \"etag\": \"\\\"FILE_ID:1\\\"\",\n    \"size_bytes\": 1024\n  },\n  \"requestId\": \"...\"\n}",
    "group": "文件"
  },
  {
    "id": "files-put",
    "method": "PUT",
    "path": "/api/file-spaces/self/content?path=backup.json",
    "title": "新建或编辑文件",
    "permission": "files:write",
    "summary": "上传原始正文。新建使用 If-None-Match: *，覆盖使用 If-Match: <当前 etag>。",
    "headers": {
      "If-None-Match": "*"
    },
    "request": "<binary body>",
    "response": "{\n  \"data\": {\n    \"id\": \"FILE_ID\",\n    \"path\": \"backup.json\",\n    \"kind\": \"file\",\n    \"visibility\": \"inherit\",\n    \"effectiveVisibility\": \"private\",\n    \"etag\": \"\\\"FILE_ID:1\\\"\",\n    \"size_bytes\": 1024\n  },\n  \"requestId\": \"...\"\n}",
    "notes": [
      "最大 100 MiB；父目录须存在",
      "覆盖时替换条件头，保留 ETag 中的双引号",
      "缺少前置条件返回 428；冲突返回 412，请重新读取并合并修改"
    ],
    "group": "文件"
  },
  {
    "id": "files-get",
    "method": "GET",
    "path": "/api/file-spaces/self/content?path=backup.json",
    "title": "下载文件",
    "permission": "files:read",
    "summary": "下载原始文件内容，支持 HEAD、单个 HTTP Range、If-Range 和 If-None-Match。",
    "response": "<binary body>",
    "notes": [
      "下载按附件返回，不缓存",
      "文本编辑限严格 UTF-8 且不超过 1 MiB，API 上传限 100 MiB"
    ],
    "group": "文件"
  },
  {
    "id": "files-head",
    "method": "HEAD",
    "path": "/api/file-spaces/self/content?path=backup.json",
    "title": "读取文件元信息",
    "permission": "files:read",
    "summary": "读取文件的响应头元信息，不返回正文。",
    "response": "<empty body>",
    "notes": [
      "返回 ETag、Content-Length、Content-Type 等元信息",
      "支持 If-None-Match"
    ],
    "group": "文件"
  },
  {
    "id": "files-update",
    "method": "PATCH",
    "path": "/api/file-spaces/self/entries/{id}",
    "title": "目录管理与公开权限",
    "permission": "files:manage / files:publish",
    "summary": "name 或 parentId 要求 manage；visibility 要求 publish；跨父目录移动同时要求两项。",
    "headers": {
      "If-Match": "\"FILE_ID:1\""
    },
    "request": "{\"visibility\":\"public\"}",
    "response": "{\n  \"data\": {\n    \"id\": \"FILE_ID\",\n    \"path\": \"backup.json\",\n    \"kind\": \"file\",\n    \"visibility\": \"inherit\",\n    \"effectiveVisibility\": \"private\",\n    \"etag\": \"\\\"FILE_ID:1\\\"\",\n    \"size_bytes\": 1024\n  },\n  \"requestId\": \"...\"\n}",
    "notes": [
      "权限为 inherit/public/private，最近显式设置优先",
      "移动仅限同空间，删除仅限管理员"
    ],
    "group": "文件"
  },
  {
    "id": "files-space-update",
    "method": "PATCH",
    "path": "/api/file-spaces/self",
    "title": "设置空间默认权限",
    "permission": "files:publish",
    "summary": "空间默认私有；更改默认权限影响所有继承它的文件。条件头使用空间 ETag。",
    "headers": {
      "If-Match": "\"domain:DOMAIN_ID:1\""
    },
    "request": "{\"visibility\":\"private\"}",
    "response": "{\"data\":{\"visibility\":\"private\",\"etag\":\"...\"}}",
    "group": "文件"
  },
  {
    "id": "files-public",
    "method": "GET",
    "path": "/api/files/{global|domainId}/{filePath}",
    "title": "公开文件链接",
    "permission": "无需 key",
    "anonymous": true,
    "summary": "按空间和完整路径下载实际公开的文件；每次请求检查权限，私有或不存在均返回 404。",
    "response": "<binary body>",
    "notes": [
      "不提供匿名目录浏览或搜索",
      "重命名或移动会改变 URL",
      "公开转私有后后续请求立即失效；每 IP 每分钟 120 次"
    ],
    "group": "文件"
  },
  {
    "id": "files-public-head",
    "method": "HEAD",
    "path": "/api/files/{global|domainId}/{filePath}",
    "title": "读取公开文件元信息",
    "permission": "无需 key",
    "anonymous": true,
    "summary": "读取公开文件的响应头元信息，不返回正文。",
    "response": "<empty body>",
    "notes": [
      "私有、不存在或所属子域停用均返回 404",
      "每次请求都会重新检查公开权限"
    ],
    "group": "文件"
  },
  {
    id: 'messages-pull', group: '下发消息', method: 'GET', path: '/api/messages?limit=20&channel=channel_a&type=json', title: '拉取下发消息', permission: 'messages:read',
    summary: '按 channel 拉取已到期且尚未确认的消息。支持重复或逗号分隔的多个 channel，并按传参顺序返回；省略 channel 或传 all 时查询全部，defaultchannel 查询默认频道。拉取不会消费消息，确认前会重复返回相同 deliveryId。type=text 时返回纯文本并包含 channel。',
    response: '{\n  "data": {\n    "items": [{\n      "deliveryId": "5d35f392-...",\n      "messageId": "b1b65f52-...",\n      "occurrence": 1,\n      "title": "刷新服务配置",\n      "contentType": "json",\n      "content": {"action": "reload"},\n      "channel": "channel_a"\n    }],\n    "polledAt": "2026-09-19T08:00:02.100Z"\n  },\n  "requestId": "..."\n}',
    notes: ['limit 默认 20，最大 50', 'channel 可重复传入或用逗号分隔，按传参顺序返回', '省略 channel、all 或 * 查询全部；default/defaultchannel 查询默认频道', 'type=text 返回 deliveryId、channel 与 content 的纯文本', '推荐轮询间隔 15–60 秒'],
  },
  {
    id: 'messages-ack', group: '下发消息', method: 'POST', path: '/api/messages/{deliveryId}/ack', title: '确认执行结果', permission: 'messages:ack',
    summary: '确认消息执行成功或失败。接口幂等，可使用相同 deliveryId 和结果安全重试。',
    request: '{\n  "status": "success",\n  "resultType": "json",\n  "result": {"reloaded": true}\n}',
    response: '{\n  "data": {\n    "acknowledged": true,\n    "completed": true\n  },\n  "requestId": "..."\n}',
    notes: ['周期消息从确认时间开始计算下一周期', '重复确认返回 idempotent: true'],
  },
  {
    id: 'messages-ack-get', group: '下发消息', method: 'GET', path: '/api/messages/{deliveryId}/ack?result=done', title: 'GET 确认执行结果', permission: 'messages:ack',
    summary: '将 result 查询参数作为文本成功结果确认消息，适合只能发起 GET 的设备。',
    response: '{\n  "data": {"acknowledged": true},\n  "requestId": "..."\n}',
    notes: ['result 位于 URL，敏感内容请使用 POST', '接口幂等'],
  },
];

const PERMISSIONS = [
  ['ddns:write', '更新所属子域 DNS'], ['data:read', '读取命名数据'], ['data:write', '写入命名数据'],
  ['events:read', '读取追加事件'], ['events:write', '追加事件'], ['logger:write', '写入 Logger 日志'],
  ['console:read', '拉取 Console 命令'], ['console:ack', '回复 Console 命令'],
  ['files:read', '列出和下载文件'], ['files:write', '条件上传和编辑文件'], ['files:manage', '管理目录与重命名'], ['files:publish', '设置公开权限'],
  ['files:global:read', '读取全局文件'], ['files:global:write', '写入全局文件'], ['files:global:manage', '管理全局目录'], ['files:global:publish', '设置全局公开权限'], ['notifications:send', '通过已配置通道发送通知'],
  ['messages:read', '拉取下发消息'], ['messages:ack', '确认消息结果'],
] as const;

const QUICK_START = `curl https://hub.188581.xyz/api/ddns \\
  -X POST \\
  -H "Authorization: Bearer \$EDGEDOCK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"ip":"1.1.1.1"}'`;

const AI_CONTEXT = `EdgeDock API base URL: https://hub.188581.xyz
Authentication: Authorization: Bearer <API_KEY>
Tenant rule: use file-spaces/self for your domain or file-spaces/global with explicit files:global:* grants. Never send another domainId.
Files: create with If-None-Match: *; edit with If-Match: <etag>. On 412, read again and merge. Public links never include keys.
Success envelope: {"data": ..., "requestId": "..."}
Error envelope: {"error":{"code":"...","message":"..."},"requestId":"..."}
Use the downloaded edgedock-openapi.yaml as the machine-readable contract. Never expose the API key.`;

export function ApiDocs() {
  const [query, setQuery] = useState('');
  const [copied, setCopied] = useState('');
  const filtered = useMemo(() => {
    const value = query.trim().toLowerCase();
    if (!value) return ENDPOINTS;
    return ENDPOINTS.filter((endpoint) => [endpoint.title, endpoint.path, endpoint.permission, endpoint.group, endpoint.summary]
      .some((item) => item.toLowerCase().includes(value)));
  }, [query]);

  const copy = async (id: string, value: string) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const input = document.createElement('textarea');
      input.value = value;
      input.style.position = 'fixed';
      input.style.opacity = '0';
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      input.remove();
    }
    setCopied(id);
    window.setTimeout(() => setCopied((current) => current === id ? '' : current), 1800);
  };

  const downloadOpenApi = () => {
    const url = URL.createObjectURL(new Blob([openApiSource], { type: 'application/yaml;charset=utf-8' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = 'edgedock-openapi.yaml';
    link.click();
    URL.revokeObjectURL(url);
  };

  return <div className="api-docs-page">
    <header className="api-docs-topbar">
      <a className="api-docs-brand" href="/overview"><span><Zap size={19} /></span><div><strong>EdgeDock</strong><small>API Reference</small></div></a>
      <div className="api-docs-top-actions">
        <button onClick={downloadOpenApi}><Download size={16} />下载 OpenAPI</button>
        <a href="/overview"><ArrowLeft size={16} />返回控制台</a>
      </div>
    </header>

    <main className="api-docs-main">
      <section className="api-docs-hero" id="top">
        <div className="api-docs-hero-copy">
          <span className="api-docs-kicker"><Sparkles size={14} />面向开发者与 AI Agent</span>
          <h1>EdgeDock API 文档</h1>
          <p>通过公开端点或绑定子域的 API key，安全调用 IP Echo、DDNS、数据、事件、文件、消息和通知能力。</p>
          <div className="api-docs-base"><span>BASE URL</span><code>https://hub.188581.xyz</code><CopyButton id="base" value="https://hub.188581.xyz" copied={copied} onCopy={copy} /></div>
        </div>
        <div className="api-docs-hero-card">
          <ShieldCheck size={24} />
          <div><strong>租户隔离内置</strong><p>API key 已绑定唯一子域。客户端无需、也不能提交或切换 <code>domain_id</code>。</p></div>
        </div>
      </section>

      <div className="api-docs-layout">
        <aside className="api-docs-toc">
          <p>文档导航</p>
          <a href="#quick-start">快速开始</a>
          <a href="#authentication">认证与权限</a>
          <a href="#endpoints">接口参考</a>
          <a href="#responses">响应与重试</a>
          <a href="#ai-context">AI 接入摘要</a>
          <div><BookOpenText size={17} /><span><strong>{ENDPOINTS.length}</strong> 个客户端操作</span></div>
        </aside>

        <div className="api-docs-content">
          <section className="api-docs-section" id="quick-start">
            <SectionHeading eyebrow="GET STARTED" title="快速开始" description="将 API key 通过环境变量注入，避免写入源码或命令历史。" />
            <CodeBlock id="quick" code={QUICK_START} copied={copied} onCopy={copy} />
            <div className="api-docs-callouts">
              <article><span>1</span><div><strong>创建最小权限 key</strong><p>每个设备使用独立 key，只授予实际需要的权限。</p></div></article>
              <article><span>2</span><div><strong>使用 Bearer Header</strong><p>无法设置 Header 时才使用 query token，并避免记录完整 URL。</p></div></article>
              <article><span>3</span><div><strong>保留 requestId</strong><p>报障时提供 requestId，永远不要提供完整 API key。</p></div></article>
            </div>
          </section>

          <section className="api-docs-section" id="authentication">
            <SectionHeading eyebrow="SECURITY" title="认证与权限" description="所有 key API 请求使用绑定单一子域的 EdgeDock API key。" />
            <div className="api-docs-auth-card"><KeyRound size={21} /><div><span>Authorization</span><code>Bearer edk_xxx</code></div><CopyButton id="auth" value="Authorization: Bearer edk_xxx" copied={copied} onCopy={copy} /></div>
            <div className="api-permission-grid">{PERMISSIONS.map(([permission, label]) => <div key={permission}><code>{permission}</code><span>{label}</span></div>)}</div>
          </section>

          <section className="api-docs-section" id="endpoints">
            <SectionHeading eyebrow="REFERENCE" title="接口参考" description="按路径、能力或权限搜索。点击接口查看请求、响应和边界条件。" />
            <div className="api-docs-search"><Search size={17} /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="搜索接口，例如 files:write、消息、/api/events" /><span>{filtered.length} / {ENDPOINTS.length}</span></div>
            <div className="api-endpoint-list">{filtered.map((endpoint) => <EndpointCard key={endpoint.id} endpoint={endpoint} copied={copied} onCopy={copy} />)}</div>
            {!filtered.length && <div className="api-docs-empty">没有匹配的接口</div>}
          </section>

          <section className="api-docs-section" id="responses">
            <SectionHeading eyebrow="CONTRACT" title="响应与重试" description="先判断 HTTP 状态码，再按稳定的 error.code 处理失败。" />
            <div className="api-response-grid">
              <article><header><Check size={16} />成功响应</header><pre>{'{\n  "data": {},\n  "requestId": "..."\n}'}</pre></article>
              <article className="error"><header><ShieldCheck size={16} />错误响应</header><pre>{'{\n  "error": {\n    "code": "PERMISSION_DENIED",\n    "message": "..."\n  },\n  "requestId": "..."\n}'}</pre></article>
            </div>
            <div className="api-retry-table">
              <div><code>400 / 404 / 409 / 413 / 415</code><span>修正请求或停止，不要原样重试</span></div>
              <div><code>401 / 403</code><span>停止调用，检查 key、子域状态和权限</span></div>
              <div><code>429</code><span>等待 error.details.resetAt 后加入随机抖动重试</span></div>
              <div><code>502 / 5xx / 网络超时</code><span>指数退避；写操作先核实结果</span></div>
            </div>
          </section>

          <section className="api-docs-section api-ai-section" id="ai-context">
            <SectionHeading eyebrow="LLM READY" title="AI 接入摘要" description="复制这一段给 AI 编程助手；完整类型和 schema 请下载 OpenAPI。" />
            <CodeBlock id="ai" code={AI_CONTEXT} copied={copied} onCopy={copy} />
            <button className="api-openapi-button" onClick={downloadOpenApi}><Download size={17} /><span><strong>下载 edgedock-openapi.yaml</strong><small>OpenAPI 3.1 · 可导入工具调用和代码生成器</small></span><ChevronRight size={18} /></button>
          </section>
        </div>
      </div>
    </main>
  </div>;
}

function SectionHeading({ eyebrow, title, description }: { eyebrow: string; title: string; description: string }) {
  return <header className="api-section-heading"><span>{eyebrow}</span><h2>{title}</h2><p>{description}</p></header>;
}

function CopyButton({ id, value, copied, onCopy }: { id: string; value: string; copied: string; onCopy: (id: string, value: string) => void }) {
  return <button className="api-copy-button" onClick={() => onCopy(id, value)} aria-label="复制到剪贴板">{copied === id ? <Check size={15} /> : <Copy size={15} />}{copied === id ? '已复制' : '复制'}</button>;
}

function CodeBlock({ id, code, copied, onCopy }: { id: string; code: string; copied: string; onCopy: (id: string, value: string) => void }) {
  return <div className="api-code-block"><div><i /><i /><i /><span>示例</span><CopyButton id={id} value={code} copied={copied} onCopy={onCopy} /></div><pre>{code}</pre></div>;
}

function EndpointCard({ endpoint, copied, onCopy }: { endpoint: Endpoint; copied: string; onCopy: (id: string, value: string) => void }) {
  const path = endpoint.path
    .replace('{id}', 'FILE_ID')
    .replace('{name}', 'device/state')
    .replace('{path}', 'backups/latest.json')
    .replace('{deliveryId}', 'DELIVERY_ID')
    .replace('{cursor}', '128')
    .replace('{type}', 'backup.completed')
    .replace('{prefix}', 'backups/')
    .replace('{global|domainId}', 'global')
    .replace('{filePath}', 'backups/latest.json')
    .replace('{text}', encodeURIComponent('备份任务已完成'));
  const body = endpoint.id === 'files-put'
    ? ` \\
  -H "Content-Type: application/octet-stream" \\
  --data-binary @backup.json`
    : endpoint.request ? ` \\
  -H "Content-Type: application/json" \\
  -d '${endpoint.request.replace(/\n\s*/g, '')}'` : '';
  const extraHeaders = Object.entries(endpoint.headers ?? {}).map(([key, value]) => ` \\
  -H '${key}: ${value}'`).join('');
  const authHeader = endpoint.anonymous ? '' : ` \\
  -H "Authorization: Bearer \$EDGEDOCK_API_KEY"`;
  const curl = `curl "https://hub.188581.xyz${path}" \\
  -X ${endpoint.method}${authHeader}${extraHeaders}${body}`;
  return <details className="api-endpoint-card" id={endpoint.id}>
    <summary>
      <span className={`api-method api-method-${endpoint.method.toLowerCase()}`}>{endpoint.method}</span>
      <code>{endpoint.path}</code>
      <div><strong>{endpoint.title}</strong><span>{endpoint.group}</span></div>
      <small>{endpoint.permission}</small>
      <ChevronRight size={17} />
    </summary>
    <div className="api-endpoint-body">
      <p>{endpoint.summary}</p>
      <div className="api-endpoint-meta"><span>所需权限</span><code>{endpoint.permission}</code></div>
      <CodeBlock id={`curl-${endpoint.id}`} code={curl} copied={copied} onCopy={onCopy} />
      <div className={`api-example-grid ${endpoint.request ? '' : 'single'}`}>
        {endpoint.request && <div><header>请求体</header><pre>{endpoint.request}</pre></div>}
        <div><header>响应示例</header><pre>{endpoint.response}</pre></div>
      </div>
      {endpoint.notes && <ul>{endpoint.notes.map((note) => <li key={note}>{note}</li>)}</ul>}
    </div>
  </details>;
}
